import { css, cx } from "@emotion/css";
import { CircularProgress, Callout } from "@octopusdeploy/design-system-components";
import { lineHeight, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { ProjectResource, SpaceResource, LicenseUsageResource, LicenseStatusResource } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import React, { useState } from "react";
import { useAnalyticSampleProjectDispatch, useAnalyticSimpleActionDispatch } from "~/analytics/Analytics";
import { generateGuid } from "~/areas/projects/components/Process/generation/index";
import { InitializeSampleProjectTour } from "~/areas/projects/components/ProjectLayout/SampleProjectTour/SampleProjectTourUtils";
import { repository, session } from "~/clientInstance";
import DataBaseComponent from "~/components/DataBaseComponent/index";
import { useDialogTrigger } from "~/components/Dialog/DialogTrigger";
import { isFeatureToggleEnabled } from "~/components/FeatureToggle/New/FeatureToggleContext";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { UpsellDialogWithLicenseUsage } from "~/components/UpsellDialog/UpsellDialogWithLicenseUsage";
import { EntitlementLimitPopUpEvent } from "~/components/UpsellDialog/upsellDialogAnalytics";
import { getLicenseLimit } from "~/hooks/useLicenseLimit";
const style = {
    loadingText: css({
        color: themeTokens.color.text.primary,
        font: text.body.regular.medium,
    }),
    loadingCallout: css({
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        paddingTop: 8,
        paddingBottom: 10,
        gap: space[12],
        opacity: 0,
    }),
    opacityAnimation: css({
        opacity: 1,
        transition: "opacity 300ms ease-in",
    }),
};
const calloutStyles = css({
    lineHeight: lineHeight.xSmall,
});
const containerStyles = css({
    margin: `${space[12]} 0`,
});
const calloutLoadingStyle = cx(style.loadingCallout, style.opacityAnimation);
interface CreateSampleProjectCalloutInternalProps {
    currentSpace: SpaceResource | undefined;
    licenseUsage: LicenseUsageResource | null;
    licenseStatus: LicenseStatusResource | null;
    busy: Promise<void> | undefined;
    onCreateSampleProject: (sampleProjectReference: string) => Promise<ProjectResource | undefined>;
}
function CreateSampleProjectCalloutInternal(props: CreateSampleProjectCalloutInternalProps) {
    const { currentSpace, licenseUsage, licenseStatus, busy, onCreateSampleProject } = props;
    const navigate = useSpaceAwareNavigation();
    const sampleProjectDispatchAction = useAnalyticSampleProjectDispatch();
    const simpleDispatchAction = useAnalyticSimpleActionDispatch();
    const isFreeTierEnabled = isFeatureToggleEnabled("free-tier");
    const { isLimitReached: isProjectLimitReached, licensedLimit } = getLicenseLimit(licenseUsage, "projects");
    const { isOpen: isProjectLimitDialogOpen, openDialog: openProjectLimitDialog, closeAllDialogs: closeProjectLimitDialog } = useDialogTrigger();
    const [loadingStyle, setLoadingStyle] = useState(style.loadingCallout);
    const clickSampleProject = async (e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault();
        if (busy) {
            return;
        }
        if (isProjectLimitReached && isFreeTierEnabled) {
            openProjectLimitDialog();
            simpleDispatchAction(EntitlementLimitPopUpEvent);
            return;
        }
        const sampleProjectReference = generateGuid();
        sampleProjectDispatchAction("Request Sample Project", {
            sampleProjectReference,
            source: "Help Sidebar",
        });
        setTimeout(() => setLoadingStyle(calloutLoadingStyle), 10);
        const project = await onCreateSampleProject(sampleProjectReference);
        if (project) {
            InitializeSampleProjectTour(project.Slug);
            navigate.navigate(links.projectOverviewRedirect.generateUrl({ spaceId: project.SpaceId, projectSlug: project.Slug }));
        }
    };
    if (!currentSpace || !session.currentPermissions?.isSpaceManager(currentSpace)) {
        return <></>;
    }
    return (<>
            <div className={containerStyles}>
                <Callout type={"new-feature"} hideTitle={true}>
                    {busy ? (<div className={loadingStyle}>
                            <CircularProgress size="small"/>
                            <div className={style.loadingText}>Loading sample project</div>
                        </div>) : (<div className={calloutStyles}>
                            <a href="#" role="link" onClick={clickSampleProject}>
                                Create a sample project
                            </a>{" "}
                            to learn about key Octopus features.
                        </div>)}
                </Callout>
            </div>
            <UpsellDialogWithLicenseUsage title="Need more projects?" content={<>
                        You've reached your <strong>{licensedLimit}-project</strong> limit. Upgrade to add more.
                    </>} open={isProjectLimitDialogOpen} onClose={closeProjectLimitDialog} licenseSerialNumber={licenseStatus?.SerialNumber} licenseUsage={licenseUsage}/>
        </>);
}
interface CreateSampleProjectCalloutState {
    currentSpace?: SpaceResource;
    licenseUsage: LicenseUsageResource | null;
    licenseStatus: LicenseStatusResource | null;
}
class CreateSampleProjectCallout extends DataBaseComponent<{}, CreateSampleProjectCalloutState> {
    constructor(props: {}) {
        super(props);
        this.state = {
            licenseUsage: null,
            licenseStatus: null,
        };
    }
    async componentDidMount() {
        await this.doBusyTask(async () => {
            if (!repository.spaceId)
                return;
            const isFreeTierEnabled = isFeatureToggleEnabled("free-tier");
            const [space, licenseUsage, licenseStatus] = await Promise.all([
                repository.Spaces.get(repository.spaceId),
                isFreeTierEnabled ? repository.Licenses.getCurrentUsage() : Promise.resolve(null),
                isFreeTierEnabled ? repository.Licenses.getCurrentStatus() : Promise.resolve(null),
            ]);
            this.setState({
                currentSpace: space,
                licenseUsage,
                licenseStatus,
            });
        });
    }
    async handleCreateSampleProject(sampleProjectReference: string): Promise<ProjectResource | undefined> {
        let project: ProjectResource | undefined = undefined;
        await this.doBusyTask(async () => {
            const sampleProject = await repository.Projects.createSampleProject("Sample Project", sampleProjectReference);
            project = sampleProject.Project;
        });
        return project;
    }
    render() {
        return (<CreateSampleProjectCalloutInternal busy={this.state.busy} currentSpace={this.state.currentSpace} licenseUsage={this.state.licenseUsage} licenseStatus={this.state.licenseStatus} onCreateSampleProject={(sampleProjectReference: string) => this.handleCreateSampleProject(sampleProjectReference)}/>);
    }
    static displayName = "CreateSampleProjectCallout";
}
export default CreateSampleProjectCallout;
